import React, { useMemo, lazy, Suspense } from 'react';
import { NavigationMenu, Provider, RoutePropagator } from '@shopify/app-bridge-react';
import { AppProvider, Frame, Spinner } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { config_variable } from '../helper/commonApi';
import { useDispatch, useSelector } from 'react-redux';
import { generateToken } from '../redux/slice/commonSlice';
import LocalMenu from '../pages/LocalMenu';
import { OnboardingGuard } from '../components/OnboardingGuard';
import { PlanGuard } from '../components/PlanGuard';
import { PlanUpgradeBanner } from '../components/PlanUpgradeBanner';

// Lazy load all page components for better performance
const DeviceBaseRule = lazy(() => import('../pages/rules/DeviceBaseRule'));
const CreateRules = lazy(() => import('../pages/CreateRules'));
const WeekEndRule = lazy(() => import('../pages/rules/WeekEndRule'));
const DayNightRule = lazy(() => import('../pages/rules/DayNightRule'));
const ProductInventoryBase = lazy(() => import('../pages/rules/ProductInventoryBase'));
const CustomerBasedPricingRule = lazy(() => import('../pages/rules/CustomerBase'));
const LocationBasedRule = lazy(() => import('../pages/rules/LocationBasedRule'));
const HomePage = lazy(() => import('../pages/HomePage'));
const OrderAnalytics = lazy(() => import('../pages/OrderAnalytics'));
const Settings = lazy(() => import('../pages/Settings'));
const FAQ = lazy(() => import('../pages/FAQ'));
const Suggestions = lazy(() => import('../pages/Suggestions'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Pricing = lazy(() => import('../pages/Pricing'));

// Loading fallback component
const LoadingFallback = () => (
    <div style={{ textAlign: "center", paddingTop: "20%" }}>
        <Spinner accessibilityLabel="Loading page" size="large" />
    </div>
);

// Helper function to wrap components with OnboardingGuard and PlanGuard
const withGuards = (Component) => (
    <OnboardingGuard>
        <PlanGuard>
            <Suspense fallback={<LoadingFallback />}>
                <Component />
            </Suspense>
        </PlanGuard>
    </OnboardingGuard>
);

// Route definitions - routes that require plan
const routeDefinitions = [
    { path: '/', component: HomePage },
    { path: '/home', component: HomePage },
    { path: '/order-analytics', component: OrderAnalytics },
    { path: '/settings', component: Settings },
    { path: '/faq', component: FAQ },
    { path: '/feedback', component: Suggestions },
    { path: '/device-base-rule', component: DeviceBaseRule },
    { path: '/device-base-rule/:id', component: DeviceBaseRule },
    { path: '/create-rules', component: CreateRules },
    { path: '/weekend-rule', component: WeekEndRule },
    { path: '/weekend-rule/:id', component: WeekEndRule },
    { path: '/daynight-rule', component: DayNightRule },
    { path: '/daynight-rule/:id', component: DayNightRule },
    { path: '/product-inventory-base-rule', component: ProductInventoryBase },
    { path: '/product-inventory-base-rule/:id', component: ProductInventoryBase },
    { path: '/customer-based-rule', component: CustomerBasedPricingRule },
    { path: '/customer-based-rule/:id', component: CustomerBasedPricingRule },
    { path: '/location-based-rule', component: LocationBasedRule },
    { path: '/location-based-rule/:id', component: LocationBasedRule },
    { path: '*', component: NotFound },
];

const pricingRoute = {
    path: '/pricing',
    element: (
        <OnboardingGuard>
            <Suspense fallback={<LoadingFallback />}>
                <Pricing />
            </Suspense>
        </OnboardingGuard>
    )
};

// Redirect /themes to /settings for backward compatibility
const themesRedirectRoute = {
    path: '/themes',
    element: <Navigate to="/settings" replace />
};

// Generate routes with guards wrapper
export const routes = [
    pricingRoute,
    themesRedirectRoute,
    ...routeDefinitions.map(route => ({
        ...route,
        element: withGuards(route.component)
    }))
];

const NAVIGATION_LINKS = [
    {
        label: 'Order Analytics',
        destination: '/order-analytics',
    },
    {
        label: 'Settings',
        destination: '/settings',
    },
    {
        label: 'FAQ',
        destination: '/faq',
    },
    {
        label: 'Feedback',
        destination: '/feedback',
    },
    {
        label: 'Pricing',
        destination: '/pricing',
    }
];

export function Router() {
    const { status } = useSelector((store) => store.commonData);
    const location = useLocation();
    const dispatch = useDispatch();

    useMemo(async () => {
        await dispatch(generateToken()).unwrap();
    }, []);

    if (!config_variable.config.host) {
        config_variable.config.host = location?.state?.config?.host || config_variable.config.host;
        config_variable.shop_name = location?.state?.shop_name || config_variable.shop_name;
    }

    const isProduction = window.location.hostname && window.location.hostname !== 'localhost';

    return (
        <AppProvider i18n={enTranslations}>
            {isProduction ? (
                !status ? (
                    <div style={{ textAlign: "center", paddingTop: "20%" }}>
                        <Spinner accessibilityLabel="Spinner example" size="large" />
                    </div>
                ) : (
                    <Provider config={config_variable.config}>
                        <NavigationMenu
                            navigationLinks={NAVIGATION_LINKS}
                            matcher={(link, location) => link.destination === location.pathname}
                        />
                        <Frame>
                            <RoutePropagator location={location} />
                            <PlanUpgradeBanner />
                            <Outlet />
                        </Frame>
                    </Provider>
                )
            ) : (
                <>
                    <LocalMenu />
                    <Outlet />
                </>
            )}
        </AppProvider>
    );
}
